import React, { createContext, FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useCookies } from "react-cookie";
import { API_BASE } from "../../config/api";

interface ChatSocketContextType {
  socket: Socket | null;
  joinOrder: (orderId: number) => void;
  leaveOrder: (orderId: number) => void;
}

export const ChatSocketContext = createContext<ChatSocketContextType>({
  socket: null,
  joinOrder: () => {},
  leaveOrder: () => {},
});

// Derives the socket.io connection target from API_BASE (config/api.ts).
// Local dev: API_BASE is absolute (http://localhost:5500) — connect directly
// to it, default socket.io path. Docker/production build: API_BASE is a
// relative path (/api, proxied by this app's own nginx, see nginx.conf) —
// connect same-origin instead, carrying that /api prefix into the socket.io
// path so the handshake reaches the same reverse-proxied route REST calls
// already use.
const getSocketConfig = (apiBase: string): { url: string; path: string } => {
  if (/^https?:\/\//i.test(apiBase)) {
    return { url: apiBase, path: "/socket.io" };
  }
  const prefix = apiBase.replace(/\/$/, "");
  return { url: window.location.origin, path: `${prefix}/socket.io` };
};

// One shared socket connection for the whole app (mounted once in App.tsx),
// matching how useSnackbar() is already a single app-level context rather
// than reconnecting per component. Connects only once an access token
// exists; disconnects and reconnects whenever the cookie's token changes
// (login/logout/refresh).
export const ChatSocketProvider: FC<PropsWithChildren> = ({ children }) => {
  const [cookies] = useCookies(["auth"]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const token = cookies.auth?.accessToken;
    if (!token) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setSocket(null);
      return;
    }

    const { url, path } = getSocketConfig(API_BASE);
    const newSocket = io(url, {
      path,
      auth: { token },
    });
    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookies.auth?.accessToken]);

  const joinOrder = useCallback((orderId: number) => {
    socketRef.current?.emit("chat:join", { order_id: orderId });
  }, []);

  const leaveOrder = useCallback((orderId: number) => {
    socketRef.current?.emit("chat:leave", { order_id: orderId });
  }, []);

  return (
    <ChatSocketContext.Provider value={{ socket, joinOrder, leaveOrder }}>
      {children}
    </ChatSocketContext.Provider>
  );
};

export default ChatSocketProvider;
